import { computed, ComputedRef, effectScope } from "vue";
import { useNowCustom } from "./useNowCustom";

/**
 * Singleton scopes to prevent multiple interval timers
 */
let nowScope: ReturnType<typeof effectScope> | null = null;
let dateScope: ReturnType<typeof effectScope> | null = null;
let midnightScope: ReturnType<typeof effectScope> | null = null;

let cachedNow: ComputedRef<number> | null = null;
let cachedDate: ComputedRef<Date> | null = null;
let cachedMidnight: ComputedRef<Date> | null = null;

/**
 * Provides reactive current timestamp in milliseconds
 * Updates every 1000ms for credential expiry checks and live status
 * Uses singleton pattern to ensure only one interval timer
 */
export function useReactiveNow(): ComputedRef<number> {
	if (cachedNow) return cachedNow;

	// Create a new scope for this singleton
	nowScope = effectScope(true);
	cachedNow = nowScope.run(() => {
		const now = useNowCustom(1000);
		return computed(() => now.value.getTime());
	})!;

	return cachedNow;
}

/**
 * Provides reactive Date object
 * Updates every 60000ms (1 minute) for date range updates
 * Uses singleton pattern to ensure only one interval timer
 */
export function useReactiveDate(): ComputedRef<Date> {
	if (cachedDate) return cachedDate;

	// Create a new scope for this singleton
	dateScope = effectScope(true);
	cachedDate = dateScope.run(() => {
		const now = useNowCustom(60000);
		return computed(() => new Date(now.value));
	})!;

	return cachedDate;
}

/**
 * Provides reactive midnight (end of today) detection
 * Automatically updates when day changes, ensuring maxDate always represents "today at 23:59:59"
 * Updates every 60000ms and checks if day changed
 * Uses singleton pattern to ensure only one interval timer
 */
export function useReactiveMidnight(): ComputedRef<Date> {
	if (cachedMidnight) return cachedMidnight;

	// Create a new scope for this singleton
	midnightScope = effectScope(true);
	cachedMidnight = midnightScope.run(() => {
		const now = useNowCustom(60000);
		return computed(() => {
			const date = new Date(now.value);
			date.setHours(23, 59, 59, 999);
			return date;
		});
	})!;

	return cachedMidnight;
}
